import * as api from '../api/api';

/**
 * Save updated role data to backend after refresh operations with enhanced error handling and batching
 */
export const persistRoleUpdates = async (rolesToUpdate, outcomeId, options = {}) => {
  if (!rolesToUpdate || rolesToUpdate.length === 0) {
    console.log('persistRoleUpdates: No roles to update, skipping persistence');
    return { success: true, updatedCount: 0 };
  }
  
  const {
    batchSize = 5, // Process 5 outcomes at a time to avoid timeouts
    maxRetries = 3,
    timeoutMs = 30000, // 30 second timeout per batch
    enableDebouncing = true
  } = options;
  
  // Debouncing to prevent duplicate executions
  if (enableDebouncing) {
    const debounceKey = 'persistRoleUpdates';
    const lastExecution = window[`${debounceKey}_lastExecution`] || 0;
    const now = Date.now();
    
    if (now - lastExecution < 500) { // 500ms debounce
      console.log('persistRoleUpdates: Debouncing - skipping duplicate execution');
      return { success: true, updatedCount: 0, debounced: true };
    }
    
    window[`${debounceKey}_lastExecution`] = now;
  }
  
  try {
    console.log(`🔄 persistRoleUpdates: Starting persistence of ${rolesToUpdate.length} updated roles to backend`);
    console.log('🔄 persistRoleUpdates: Roles to update:', rolesToUpdate.map(r => `${r.updatedRole?.name || 'Unknown'} (${r.updatedRole?.roster_member_id || 'No ID'})`));
    
    // Group roles by outcome if we have that information
    const rolesByOutcome = new Map();
    
    rolesToUpdate.forEach(({ updatedRole, originalRole }) => {
      const targetOutcomeId = updatedRole.outcome_id || originalRole.outcome_id || outcomeId;
      if (targetOutcomeId) {
        if (!rolesByOutcome.has(targetOutcomeId)) {
          rolesByOutcome.set(targetOutcomeId, []);
        }
        rolesByOutcome.get(targetOutcomeId).push({ updatedRole, originalRole });
      }
    });
    
    const outcomeEntries = Array.from(rolesByOutcome.entries());
    console.log(`🔄 persistRoleUpdates: Processing ${outcomeEntries.length} outcomes in batches of ${batchSize}`);
    
    // Process outcomes in batches to avoid overwhelming the backend
    const allResults = [];
    for (let i = 0; i < outcomeEntries.length; i += batchSize) {
      const batch = outcomeEntries.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(outcomeEntries.length / batchSize);
      
      console.log(`🔄 persistRoleUpdates: Processing batch ${batchNumber}/${totalBatches}: ${batch.length} outcomes`);
      
      // Process batch with timeout and retry logic
      const batchPromises = batch.map(([outId, roleUpdates]) => 
        persistOutcomeWithRetry(outId, roleUpdates, maxRetries, timeoutMs)
      );
      
      try {
        const batchResults = await Promise.allSettled(batchPromises);
        
        // Process batch results
        batchResults.forEach((result, index) => {
          const [outId] = batch[index];
          if (result.status === 'fulfilled') {
            allResults.push(result.value);
            console.log(`✅ persistRoleUpdates: Batch ${batchNumber} - Outcome ${outId} succeeded`);
          } else {
            console.error(`❌ persistRoleUpdates: Batch ${batchNumber} - Outcome ${outId} failed:`, result.reason);
            allResults.push({ 
              outcomeId: outId, 
              updatedCount: 0, 
              error: result.reason?.message || 'Unknown error',
              errorType: categorizeError(result.reason)
            });
          }
        });
        
        // Add delay between batches to avoid overwhelming the backend
        if (i + batchSize < outcomeEntries.length) {
          console.log(`⏳ persistRoleUpdates: Waiting 1s before next batch...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
      } catch (batchError) {
        console.error(`❌ persistRoleUpdates: Batch ${batchNumber} failed completely:`, batchError);
        // Add failed results for this batch
        batch.forEach(([outId]) => {
          allResults.push({ 
            outcomeId: outId, 
            updatedCount: 0, 
            error: batchError.message,
            errorType: categorizeError(batchError)
          });
        });
      }
    }
    
    // Summarize results
    const totalUpdated = allResults.reduce((sum, result) => sum + (result.updatedCount || 0), 0);
    const successfulOutcomes = allResults.filter(r => r.updatedCount > 0);
    const failedOutcomes = allResults.filter(r => r.error);
    const updatedOutcomeIds = successfulOutcomes.map(r => r.outcomeId);
    
    console.log(`persistRoleUpdates: Completed - ${totalUpdated} roles updated across ${successfulOutcomes.length} outcomes, ${failedOutcomes.length} failures`);
    
    if (failedOutcomes.length > 0) {
      console.warn(`persistRoleUpdates: Failed outcomes:`, failedOutcomes.map(f => `${f.outcomeId}: ${f.error} (${f.errorType})`));
    }
    
    // Cache the persistence data locally to bridge backend sync gaps
    rolesToUpdate.forEach(({ updatedRole, originalRole }) => {
      const roleKey = `${updatedRole.outcome_id}-${updatedRole.roster_member_id}-${updatedRole.role || 'unknown'}`;
      cacheRolePersistenceData(roleKey, {
        rate: updatedRole.rate,
        lastRateUpdate: updatedRole.lastRateUpdate,
        lastMetadataUpdate: updatedRole.lastMetadataUpdate,
        calculatedForYear: updatedRole.calculatedForYear,
        rateAdjustmentInfo: updatedRole.rateAdjustmentInfo,
        rateType: updatedRole.rateType,
        annualWorkingDays: updatedRole.annualWorkingDays
      });
    });
    
    // Notify other components that outcome rates have been updated
    if (updatedOutcomeIds.length > 0) {
      notifyOutcomeRatesUpdated(updatedOutcomeIds, {
        totalUpdated,
        source: 'roster_silent_refresh',
        results: allResults,
        batchProcessed: true
      });
      
      // Invalidate caches to force fresh data fetches
      invalidateOutcomeCaches(updatedOutcomeIds);
    }
    
    return {
      success: successfulOutcomes.length > 0,
      updatedCount: totalUpdated,
      results: allResults,
      updatedOutcomeIds,
      successfulOutcomes: successfulOutcomes.length,
      failedOutcomes: failedOutcomes.length,
      batchProcessed: true
    };
  } catch (error) {
    console.error('❌ persistRoleUpdates: Critical error persisting role updates:', error);
    console.error('❌ persistRoleUpdates: Error context:', {
      message: error.message,
      stack: error.stack,
      rolesToUpdateCount: rolesToUpdate?.length || 0,
      outcomeId: outcomeId
    });
    return {
      success: false,
      error: error.message,
      updatedCount: 0,
      criticalError: true,
      errorType: categorizeError(error)
    };
  }
};

/**
 * Persist outcome with retry logic and timeout handling
 */
const persistOutcomeWithRetry = async (outcomeId, roleUpdates, maxRetries, timeoutMs) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const corruptionCleanups = roleUpdates.filter(({ updatedRole, originalRole }) => 
        originalRole.corruptionCleaned || originalRole.needsPersistence
      ).length;
      
      console.log(`persistOutcomeWithRetry: Attempt ${attempt}/${maxRetries} - Updating ${roleUpdates.length} roles for outcome ${outcomeId} (${corruptionCleanups} corruption cleanups)`);
      
            // Prepare role updates for the API - ensure we have the correct structure
      const apiRoleUpdates = roleUpdates.map(({ updatedRole, originalRole }) => {
        // Find the role ID from the original role or use a fallback
        const roleId = originalRole.id || originalRole.role_id || `${outcomeId}-${updatedRole.roster_member_id}-${Date.now()}`;
        
        const apiUpdate = {
          role_id: roleId,
          roster_member_id: updatedRole.roster_member_id || originalRole.roster_member_id,
          rate: updatedRole.rate || 0,
          company: updatedRole.company || originalRole.company,
          location: updatedRole.location || originalRole.location,
          function: updatedRole.function || originalRole.function,
          role: updatedRole.role || originalRole.role,
          focus: updatedRole.focus || originalRole.focus,
          department: updatedRole.department || originalRole.department,
          rateAdjustmentInfo: updatedRole.rateAdjustmentInfo,
          lastRateUpdate: updatedRole.lastRateUpdate,
          lastMetadataUpdate: updatedRole.lastMetadataUpdate,
          calculatedForYear: updatedRole.calculatedForYear,
          rateType: updatedRole.rateType,
          annualWorkingDays: updatedRole.annualWorkingDays
        };
        
        // Explicitly clear corrupted adjustment info but preserve valid fields
        if (originalRole.corruptionCleaned || originalRole.needsPersistence) {
          apiUpdate.rateAdjustmentInfo = null;
          // Don't clear calculatedForYear if the updated role has a valid year
          if (!updatedRole.calculatedForYear) {
            apiUpdate.calculatedForYear = null;
          }
          // Don't clear lastRateUpdate if the updated role has a valid timestamp
          if (!updatedRole.lastRateUpdate) {
            apiUpdate.lastRateUpdate = null;
          }
          console.log(`🧹 persistOutcomeWithRetry: Clearing corrupted rateAdjustmentInfo for ${originalRole.name}, preserving valid fields`);
        }
        
                return apiUpdate;
      });
      
      // Validate that we have valid data to persist
      const validUpdates = apiRoleUpdates.filter(update => 
        update.roster_member_id && update.role_id
      );
      
      if (validUpdates.length === 0) {
        console.warn(`persistOutcomeWithRetry: No valid updates for outcome ${outcomeId}, skipping`);
        return { 
          outcomeId, 
          updatedCount: 0, 
          result: { message: 'No valid updates to persist' },
          corruptionCleanups: 0,
          attempt,
          skipped: true
        };
      }
      
      if (validUpdates.length < apiRoleUpdates.length) {
        console.warn(`persistOutcomeWithRetry: Filtered ${apiRoleUpdates.length - validUpdates.length} invalid updates for outcome ${outcomeId}`);
      }
      
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs);
      });
      
      // Race between API call and timeout
      const apiPromise = api.persistOutcomeRoleUpdates(outcomeId, validUpdates);
      const result = await Promise.race([apiPromise, timeoutPromise]);
      
      console.log(`✅ persistOutcomeWithRetry: Attempt ${attempt} succeeded for outcome ${outcomeId}`);
      if (corruptionCleanups > 0) {
        console.log(`✅ persistOutcomeWithRetry: Persisted ${corruptionCleanups} corruption cleanups for outcome ${outcomeId}`);
      }
      
      return { 
        outcomeId, 
        updatedCount: validUpdates.length, 
        result, 
        corruptionCleanups,
        attempt
      };
      
    } catch (error) {
      const errorType = categorizeError(error);
      const isLastAttempt = attempt === maxRetries;
      
            console.error(`❌ persistOutcomeWithRetry: Attempt ${attempt}/${maxRetries} failed for outcome ${outcomeId}:`, {
        error: error.message,
        errorType,
        isLastAttempt,
        stack: error.stack?.split('\n').slice(0, 3).join('\n') // First 3 lines of stack
      });
      
      if (isLastAttempt) {
        throw error; // Re-throw on last attempt
      }
      
      // Wait before retry (exponential backoff)
      const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Max 5s wait
      console.log(`⏳ persistOutcomeWithRetry: Waiting ${waitTime}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
};

/**
 * Categorize errors for better debugging and handling
 */
const categorizeError = (error) => {
  if (!error) return 'unknown';
  
  const message = error.message?.toLowerCase() || '';
  
  if (message.includes('timeout')) return 'timeout';
  if (message.includes('network') || message.includes('fetch')) return 'network';
  if (message.includes('404')) return 'endpoint_missing';
  if (message.includes('500') || message.includes('internal server')) return 'server_error';
  if (message.includes('400') || message.includes('bad request')) return 'bad_request';
  if (message.includes('401') || message.includes('unauthorized')) return 'unauthorized';
  if (message.includes('403') || message.includes('forbidden')) return 'forbidden';
  
  return 'unknown';
};

// Cache for COLA rate to avoid repeated API calls
let colaRateCache = null;
let colaRateCacheTime = null;
const COLA_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for role persistence data to bridge backend sync gaps
const rolePersistenceCache = new Map();
const PERSISTENCE_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Cache role persistence data locally
 */
export const cacheRolePersistenceData = (roleKey, persistenceData) => {
  rolePersistenceCache.set(roleKey, {
    ...persistenceData,
    cachedAt: Date.now()
  });
};

/**
 * Get cached role persistence data
 */
export const getCachedRolePersistenceData = (roleKey) => {
  const cached = rolePersistenceCache.get(roleKey);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.cachedAt > PERSISTENCE_CACHE_DURATION) {
    rolePersistenceCache.delete(roleKey);
    return null;
  }
  
  return cached;
};

/**
 * Clear persistence cache for specific outcomes
 */
export const clearPersistenceCacheForOutcomes = (outcomeIds) => {
  if (!Array.isArray(outcomeIds)) return;
  
  const keysToDelete = [];
  for (const [key, data] of rolePersistenceCache.entries()) {
    if (outcomeIds.some(outcomeId => key.includes(outcomeId))) {
      keysToDelete.push(key);
    }
  }
  
  keysToDelete.forEach(key => rolePersistenceCache.delete(key));
  console.log(`clearPersistenceCacheForOutcomes: Cleared ${keysToDelete.length} cached entries for outcomes:`, outcomeIds);
};

/**
 * Get COLA rate with caching
 */
export const getCOLARate = async () => {
  const now = Date.now();
  
  if (colaRateCache && colaRateCacheTime && (now - colaRateCacheTime) < COLA_CACHE_DURATION) {
    return colaRateCache;
  }
  
  try {
    const settings = await api.getCOLASettings();
    colaRateCache = settings.annual_cola_percentage || 4.5;
    colaRateCacheTime = now;
    return colaRateCache;
  } catch (error) {
    console.warn('Could not fetch COLA rate, using default 4.5%');
    colaRateCache = 4.5;
    colaRateCacheTime = now;
    return 4.5;
  }
};

/**
 * Calculate COLA-adjusted rate (works with any rate type)
 */
export const calculateCOLAAdjustedRate = (baseRate, baseYear, targetYear, colaRate) => {
  if (baseYear >= targetYear) return baseRate;
  const yearsDiff = targetYear - baseYear;
  const colaMultiplier = Math.pow(1 + colaRate / 100, yearsDiff);
  return baseRate * colaMultiplier;
};

/**
 * Apply COLA to original rate then convert to monthly
 */
export const applyCOLAAndConvertToMonthly = (baseRate, baseYear, targetYear, colaRate, annualWorkingDays) => {
  // Validate inputs to prevent unrealistic calculations
  if (!baseRate || baseRate <= 0) {
    console.warn('applyCOLAAndConvertToMonthly: Invalid base rate:', baseRate);
    return 0;
  }
  
  // Use the validation helper for consistent annualWorkingDays handling
  const validatedWorkingDays = validateAnnualWorkingDays(annualWorkingDays, 'applyCOLAAndConvertToMonthly');
  
  // Detect corrupted data: if baseRate looks like annual (>50k) but annualWorkingDays suggests daily rate (>200)
  if (baseRate > 50000 && validatedWorkingDays > 200) {
    console.warn(`applyCOLAAndConvertToMonthly: Detected corrupted data: baseRate ${baseRate} with workingDays ${validatedWorkingDays}`);
    console.warn(`applyCOLAAndConvertToMonthly: This suggests annual rate stored with daily rate working days - treating as annual`);
    // Treat as annual rate by setting working days to 1
    const correctedWorkingDays = 1;
    const colaAdjustedRate = calculateCOLAAdjustedRate(baseRate, baseYear, targetYear, colaRate);
    return convertToMonthly(colaAdjustedRate, correctedWorkingDays);
  }
  
  // First apply COLA to the original rate (whatever type it is)
  const colaAdjustedRate = calculateCOLAAdjustedRate(baseRate, baseYear, targetYear, colaRate);
  
  // Validate COLA-adjusted rate before conversion
  if (colaAdjustedRate > 1000000) { // 1M threshold for daily rates
    console.warn(`applyCOLAAndConvertToMonthly: Unrealistic COLA-adjusted rate ${colaAdjustedRate}, using base rate instead`);
    return convertToMonthly(baseRate, annualWorkingDays);
  }
  
  // Then convert to monthly using validated working days
  const monthlyRate = convertToMonthly(colaAdjustedRate, validatedWorkingDays);
  
  // Final validation of monthly rate
  if (monthlyRate > 100000) { // 100k monthly is very high but possible for senior roles
    console.warn(`applyCOLAAndConvertToMonthly: High monthly rate ${monthlyRate} calculated from base ${baseRate} (${baseYear}) to ${targetYear}`);
  }
  
  return monthlyRate;
};

let _ratesCacheData = null;
let _ratesCacheTime = 0;
let _ratesFetchPromise = null;

const _getCachedRates = async () => {
  if (_ratesCacheData && Date.now() - _ratesCacheTime < 60000) {
    return _ratesCacheData;
  }
  if (!_ratesFetchPromise) {
    _ratesFetchPromise = api.getRates().then(data => {
      _ratesCacheData = data;
      _ratesCacheTime = Date.now();
      _ratesFetchPromise = null;
      return data;
    }).catch(err => {
      _ratesFetchPromise = null;
      throw err;
    });
  }
  return _ratesFetchPromise;
};

/**
 * Get rate for specific company, location, role, and year with COLA adjustment
 */
export const getRateWithCOLA = async (company, location, role, targetYear) => {
  try {
    const [ratesResponse, colaRate] = await Promise.all([
      _getCachedRates(),
      getCOLARate()
    ]);
    
    const rates = ratesResponse?.rates || [];
    let isFuzzyMatch = false;
    let fuzzyMatchInfo = null;
    
    // Check if we're in quiet mode to reduce console noise during batch operations
    const isQuietMode = typeof window !== 'undefined' && window.rateCalculationQuietMode;
    
    if (!isQuietMode) {
      console.log(`getRateWithCOLA: Searching ${rates.length} rates for ${company}/${location}/${role} (target year: ${targetYear})`);
      console.log('Available rates:', rates.map(r => ({ company: r.company, location: r.location, role: r.role, year: r.year || 2025 })));
    }
    
    // First, try to find exact year match
    const exactMatch = rates.find(rate => 
      rate.company === company && 
      rate.location === location && 
      rate.role === role && 
      (rate.year || 2025) === targetYear
    );
    
        if (exactMatch) {
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: Found exact match for year ${targetYear}:`, exactMatch);
      }
      return {
        ...exactMatch,
        isAdjusted: false,
        adjustmentInfo: null,
        isFuzzyMatch: false,
        fuzzyMatchInfo: null
      };
    }
    
    if (!isQuietMode) {
      console.log(`getRateWithCOLA: No exact match found for ${company}/${location}/${role} year ${targetYear}`);
    }
    
    // Find all matching rates (same company, location, role) and get most recent
    let matchingRates = rates.filter(rate => 
      rate.company === company && 
      rate.location === location && 
      rate.role === role
    );
    
        if (!isQuietMode) {
      console.log(`getRateWithCOLA: Found ${matchingRates.length} matching rates for ${company}/${location}/${role}:`, 
        matchingRates.map(r => ({ year: r.year || 2025, rate: r.dailyRate })));
    }
    
        // If no exact role match, try fuzzy matching
    if (matchingRates.length === 0 && role && role.trim() !== '') {
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: No exact role match for '${role}', trying fuzzy matching`);
      }
      
      // Try partial matches (case insensitive)
      const fuzzyMatches = rates.filter(rate => 
        rate.company === company && 
        rate.location === location && 
        (rate.role.toLowerCase().includes(role.toLowerCase()) || 
         role.toLowerCase().includes(rate.role.toLowerCase()))
      );
      
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: Found ${fuzzyMatches.length} fuzzy matches:`, 
          fuzzyMatches.map(r => ({ role: r.role, year: r.year || 2025, rate: r.dailyRate })));
      }
      
      if (fuzzyMatches.length > 0) {
        matchingRates = fuzzyMatches;
        if (!isQuietMode) {
          console.log(`getRateWithCOLA: Using fuzzy matches`);
        }
        // Mark that we used fuzzy matching
        isFuzzyMatch = true;
        fuzzyMatchInfo = {
          originalRole: role,
          matchedRole: fuzzyMatches[0].role,
          matchCount: fuzzyMatches.length
        };
      }
    }
    
        // If still no match and role is empty, try to find a generic rate for this company/location
    if (matchingRates.length === 0) {
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: No role matches found, looking for generic rates for ${company}/${location}`);
      }
      
      // Look for any rate with this company/location combination
      const genericRates = rates.filter(rate => 
        rate.company === company && 
        rate.location === location
      );
      
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: Found ${genericRates.length} generic rates for ${company}/${location}:`, 
          genericRates.map(r => ({ role: r.role, year: r.year || 2025, rate: r.dailyRate })));
      }
      
      if (genericRates.length > 0) {
        // Use the most recent generic rate
        genericRates.sort((a, b) => (b.year || 2025) - (a.year || 2025));
        matchingRates = [genericRates[0]];
        if (!isQuietMode) {
          console.log(`getRateWithCOLA: Using generic rate:`, matchingRates[0]);
        }
      }
    }
    
        if (!isQuietMode) {
      console.log(`getRateWithCOLA: Found ${matchingRates.length} matching rates:`, matchingRates.map(r => ({ role: r.role, year: r.year || 2025, rate: r.dailyRate })));
    }
    
    if (matchingRates.length === 0) {
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: No matching rates found for ${company}/${location}/${role}`);
      }
      
      // As a last resort, try to find any rate for this company (regardless of location)
      const companyRates = rates.filter(rate => rate.company === company);
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: Found ${companyRates.length} company-wide rates for ${company}:`, 
          companyRates.map(r => ({ location: r.location, role: r.role, year: r.year || 2025, rate: r.dailyRate })));
      }
      
      if (companyRates.length > 0) {
        // Use the most recent company-wide rate
        companyRates.sort((a, b) => (b.year || 2025) - (a.year || 2025));
        matchingRates = [companyRates[0]];
        if (!isQuietMode) {
          console.log(`getRateWithCOLA: Using company-wide fallback rate:`, matchingRates[0]);
        }
      } else {
        return null;
      }
    }
    
        // Sort by year (treat missing year as 2025) and get most recent
    matchingRates.sort((a, b) => (b.year || 2025) - (a.year || 2025));
    const baseRate = matchingRates[0];
    const baseYear = baseRate.year || 2025;
    
    if (!isQuietMode) {
      console.log(`getRateWithCOLA: Using base rate from year ${baseYear}:`, baseRate);
    }
    
    // If base year is same as target year, return as is
    if (baseYear === targetYear) {
      if (!isQuietMode) {
        console.log(`getRateWithCOLA: Base year ${baseYear} matches target year ${targetYear}, no adjustment needed`);
      }
      return {
        ...baseRate,
        isAdjusted: false,
        adjustmentInfo: null,
        isFuzzyMatch,
        fuzzyMatchInfo
      };
    }
    
    // Apply COLA adjustment
    const adjustedDailyRate = calculateCOLAAdjustedRate(baseRate.dailyRate, baseYear, targetYear, colaRate);
    if (!isQuietMode) {
      console.log(`getRateWithCOLA: COLA adjustment: ${baseRate.dailyRate} (${baseYear}) -> ${adjustedDailyRate} (${targetYear}) using ${colaRate}% COLA`);
    }
    
    return {
      ...baseRate,
      dailyRate: adjustedDailyRate,
      year: targetYear,
      isAdjusted: true,
      adjustmentInfo: {
        baseYear,
        baseRate: baseRate.dailyRate,
        colaRate,
        yearsDiff: targetYear - baseYear,
        adjustmentMultiplier: Math.pow(1 + colaRate / 100, targetYear - baseYear),
        rateType: determineRateType(baseRate.annualWorkingDays),
        annualWorkingDays: baseRate.annualWorkingDays
      },
      isFuzzyMatch,
      fuzzyMatchInfo
    };
    
  } catch (error) {
    console.error('Error getting rate with COLA:', error);
    return null;
  }
};

/**
 * Get rate for role configuration with COLA adjustment and monthly conversion
 */
export const getRateForRole = async (company, location, role, targetYear) => {
  try {
    console.log(`getRateForRole: Looking up rate for ${company}/${location}/${role} for year ${targetYear}`);
    const rateData = await getRateWithCOLA(company, location, role, targetYear);
    
    if (!rateData) {
      console.log(`getRateForRole: No rate data found for ${company}/${location}/${role}`);
      return {
        monthlyRate: 0,
        isAdjusted: false,
        adjustmentInfo: null
      };
    }
    
    // Apply COLA to original rate then convert to monthly
    let monthlyRate;
    if (rateData.isAdjusted && rateData.adjustmentInfo) {
      // Use COLA-adjusted rate and convert to monthly
      monthlyRate = applyCOLAAndConvertToMonthly(
        rateData.adjustmentInfo.baseRate,
        rateData.adjustmentInfo.baseYear,
        targetYear,
        rateData.adjustmentInfo.colaRate,
        rateData.annualWorkingDays
      );
    } else {
      // Convert original rate to monthly
      monthlyRate = convertToMonthly(rateData.dailyRate, rateData.annualWorkingDays);
    }
    
    console.log(`getRateForRole: Found rate for ${company}/${location}/${role}: ${monthlyRate}/month, isAdjusted: ${rateData.isAdjusted}`);
    
    return {
      monthlyRate,
      isAdjusted: rateData.isAdjusted,
      adjustmentInfo: rateData.adjustmentInfo,
      originalRate: rateData,
      isFuzzyMatch: rateData.isFuzzyMatch,
      fuzzyMatchInfo: rateData.fuzzyMatchInfo
    };
    
  } catch (error) {
    console.error('Error getting rate for role:', error);
    return {
      monthlyRate: 0,
      isAdjusted: false,
      adjustmentInfo: null
    };
  }
};

/**
 * Convert any rate type to monthly rate based on annualWorkingDays
 */
export const convertToMonthly = (rate, annualWorkingDays = 252) => {
  // Validate inputs
  if (!rate || rate <= 0) {
    return 0;
  }
  
  // Use the validation helper to ensure consistent handling
  const validatedWorkingDays = validateAnnualWorkingDays(annualWorkingDays, 'convertToMonthly');
  
  let monthlyRate;
  
  if (validatedWorkingDays === 1) {
    // Annual rate → Monthly
    monthlyRate = Math.round(rate / 12);
  } else if (validatedWorkingDays === 12) {
    // Already monthly rate
    monthlyRate = Math.round(rate);
  } else if (validatedWorkingDays >= 60 && validatedWorkingDays <= 93) {
    // Quarterly rate → Monthly (assume 3 months per quarter)
    monthlyRate = Math.round(rate / 3);
  } else if (validatedWorkingDays >= 200) {
    // Daily rate → Monthly (21 business days per month)
    const BUSINESS_DAYS_PER_MONTH = 21;
    monthlyRate = Math.round(rate * BUSINESS_DAYS_PER_MONTH);
    
    // Additional validation for daily rate conversions
    if (rate > 5000 && monthlyRate > 100000) {
      console.warn(`convertToMonthly: High daily rate ${rate} converted to monthly ${monthlyRate} - this might be incorrect`);
      console.warn(`convertToMonthly: Check if rate ${rate} is actually a daily rate or if annualWorkingDays ${validatedWorkingDays} is correct`);
    }
  } else {
    // Default: treat as monthly
    console.warn(`convertToMonthly: Unknown rate type with ${validatedWorkingDays} working days, treating as monthly`);
    monthlyRate = Math.round(rate);
  }
  
  // Final sanity check
  if (monthlyRate > 200000) {
    console.error(`convertToMonthly: Extremely high monthly rate ${monthlyRate} calculated from rate ${rate} with ${annualWorkingDays} working days`);
    console.error(`convertToMonthly: This likely indicates a data or calculation error`);
  }
  
  return monthlyRate;
};

/**
 * Legacy function for backward compatibility
 */
export const convertDailyToMonthly = (dailyRate, annualWorkingDays = 252) => {
  return convertToMonthly(dailyRate, annualWorkingDays);
};

/**
 * Validate and normalize annualWorkingDays to prevent calculation errors
 */
export const validateAnnualWorkingDays = (annualWorkingDays, context = 'unknown') => {
  // Handle null, undefined, or zero values
  if (!annualWorkingDays || annualWorkingDays <= 0) {
    console.warn(`validateAnnualWorkingDays: Invalid annualWorkingDays ${annualWorkingDays} in ${context}, defaulting to 252`);
    return 252;
  }
  
  // Handle unrealistic values
  if (annualWorkingDays > 365) {
    console.warn(`validateAnnualWorkingDays: Unrealistic annualWorkingDays ${annualWorkingDays} in ${context}, capping to 365`);
    return 365;
  }
  
  // Handle very small values that might be incorrect
  if (annualWorkingDays < 1 && annualWorkingDays > 0) {
    console.warn(`validateAnnualWorkingDays: Very small annualWorkingDays ${annualWorkingDays} in ${context}, treating as annual rate (1)`);
    return 1;
  }
  
  return annualWorkingDays;
};

/**
 * Determine the type of rate based on annualWorkingDays
 */
export const determineRateType = (annualWorkingDays) => {
  const validatedDays = validateAnnualWorkingDays(annualWorkingDays, 'determineRateType');
  
  if (validatedDays === 1) return 'annual';
  if (validatedDays === 12) return 'monthly';
  if (validatedDays >= 60 && validatedDays <= 93) return 'quarterly';
  if (validatedDays >= 200) return 'daily';
  return 'unknown';
};

/**
 * Get all available rates for a company/location combination with year info
 */
export const getAvailableRatesForLocation = async (company, location) => {
  try {
    const response = await api.getRates();
    const rates = response?.rates || [];
    
    return rates.filter(rate => 
      rate.company === company && 
      rate.location === location
    ).map(rate => ({
      ...rate,
      year: rate.year || 2025
    }));
  } catch (error) {
    console.error('Error getting available rates:', error);
    return [];
  }
};

/**
 * Extract year from various date formats
 */
export const extractYearFromDate = (dateValue, fallbackYear = new Date().getFullYear()) => {
  if (!dateValue) return fallbackYear;
  
  if (typeof dateValue === 'number') return dateValue;
  
  if (typeof dateValue === 'string') {
    // Handle YYYY-MM format
    if (dateValue.includes('-')) {
      const year = parseInt(dateValue.split('-')[0]);
      return isNaN(year) ? fallbackYear : year;
    }
    
    // Handle YYYY format
    const year = parseInt(dateValue);
    return isNaN(year) ? fallbackYear : year;
  }
  
  return fallbackYear;
};

/**
 * Get project year from outcome data
 */
export const getProjectYear = (outcomeData, costEstimationData, fallbackYear = new Date().getFullYear()) => {
  // Debug logging to understand what data we have
  console.log('getProjectYear: Debug data availability:', {
    hasOutcomeData: !!outcomeData,
    outcomeStartDate: outcomeData?.start_date,
    hasMilestones: !!(outcomeData?.milestones?.length),
    firstMilestoneStartDate: outcomeData?.milestones?.[0]?.start_date,
    hasCostEstimationData: !!costEstimationData,
    costEstimationStartMonth: costEstimationData?.start_month,
    fallbackYear
  });
  
  // Priority order: outcome start_date, first milestone start_date, cost estimation start_month, fallback
  if (outcomeData?.start_date) {
    const year = extractYearFromDate(outcomeData.start_date, fallbackYear);
    console.log('getProjectYear: Using outcome start_date:', outcomeData.start_date, '-> year:', year);
    return year;
  }
  
  // If no outcome start_date, check if we have milestone dates
  if (outcomeData?.milestones?.length > 0) {
    const firstMilestone = outcomeData.milestones[0];
    if (firstMilestone.start_date) {
      const year = extractYearFromDate(firstMilestone.start_date, fallbackYear);
      console.log('getProjectYear: Using first milestone start_date:', firstMilestone.start_date, '-> year:', year);
      return year;
    }
  }
  
  if (costEstimationData?.start_month) {
    const year = extractYearFromDate(costEstimationData.start_month, fallbackYear);
    console.log('getProjectYear: Using cost estimation start_month:', costEstimationData.start_month, '-> year:', year);
    return year;
  }
  
  console.log('getProjectYear: Using fallback year:', fallbackYear);
  return fallbackYear;
};

/**
 * Get project year for a specific role based on its timeline
 * This ensures COLA is calculated for the year when the role is actually active
 */
export const getRoleProjectYear = (role, milestone, outcomeData, costEstimationData, fallbackYear = new Date().getFullYear()) => {
  // Priority 1: If role has dateRanges, calculate from project start + role start month
  if (role.dateRanges && role.dateRanges.length > 0) {
    const firstRange = role.dateRanges[0];
    
    // Try costEstimationData.start_month first
    if (costEstimationData?.start_month) {
      // Parse the start month (format: YYYY-MM)
      const [projectYear, projectMonth] = costEstimationData.start_month.split('-').map(Number);
      
      // Calculate role start month: role month 1 = project start month, role month 2 = project start + 1, etc.
      const roleStartMonth = projectMonth + (firstRange.startMonth - 1);
      
      // Calculate the year, accounting for month overflow
      let roleYear = projectYear;
      if (roleStartMonth > 12) {
        roleYear += Math.floor((roleStartMonth - 1) / 12);
      } else if (roleStartMonth < 1) {
        roleYear += Math.floor((roleStartMonth - 12) / 12);
      }
      
      console.log(`getRoleProjectYear: Using role dateRange with costEstimation start: project start ${costEstimationData.start_month}, role month ${firstRange.startMonth} -> year ${roleYear}`);
      return roleYear;
    }
    
    // Try outcomeData.start_date as fallback
    if (outcomeData?.start_date) {
      // Parse the start date to get year and month
      const projectStartDate = new Date(outcomeData.start_date);
      const projectYear = projectStartDate.getFullYear();
      const projectMonth = projectStartDate.getMonth() + 1; // Convert to 1-based month
      
      // Calculate role start month: role month 1 = project start month, role month 2 = project start + 1, etc.
      const roleStartMonth = projectMonth + (firstRange.startMonth - 1);
      
      // Calculate the year, accounting for month overflow
      let roleYear = projectYear;
      if (roleStartMonth > 12) {
        roleYear += Math.floor((roleStartMonth - 1) / 12);
      } else if (roleStartMonth < 1) {
        roleYear += Math.floor((roleStartMonth - 12) / 12);
      }
      
      console.log(`getRoleProjectYear: Using role dateRange with outcome start: project start ${outcomeData.start_date}, role month ${firstRange.startMonth} -> year ${roleYear}`);
      return roleYear;
    }
  }
  
  // Priority 2: If milestone has specific dates, use milestone start year
  if (milestone?.start_date) {
    const year = extractYearFromDate(milestone.start_date, fallbackYear);
    console.log('getRoleProjectYear: Using milestone start_date:', milestone.start_date, '-> year:', year);
    return year;
  }
  
  // Priority 3: Fall back to general project year
  const projectYear = getProjectYear(outcomeData, costEstimationData, fallbackYear);
  console.log('getRoleProjectYear: Using general project year:', projectYear);
  return projectYear;
};

/**
 * Role mapping for standardizing roster roles to rate roles
 * This ensures consistent mapping between IT roster member roles and rate table roles
 */
const ROLE_MAPPING = {
  // CA role mappings
  'Sr Dev': 'Fullstack Developer',
  'Jr Dev': 'Fullstack Developer',
  'Senior BA': 'Business Analyst',
  'Product Manager': 'Product',
  'UX Designer': 'UX Design',
  'Tech Lead': 'Tech Lead',
  'Architect': 'Tech Lead',
  'QA Lead': 'QA',
  'Jr QA': 'QA',
  'Business Analyst': 'Business Analyst',
  'Director': 'Product',
  'Senior Director': 'Product',
  'Managing Director, Information Technology': 'Product',
  'Investment System User Support Associate': 'Business Analyst',
  'Investment System Support Senior Analyst': 'Business Analyst',
  'Investment Systems User Support Analyst': 'Business Analyst',
  'Investment Systems Support Manager': 'Business Analyst',
  'Senior Business Analyst, CRM': 'Business Analyst',
  'Senior Business Analyst': 'Business Analyst',
  'Business Analyst Intern': 'Business Analyst',
  'Senior Business Systems Analyst': 'Business Analyst',
  'Business Systems Analyst': 'Business Analyst',
  'Digital Engagement Service Delivery Manager': 'Business Analyst',
  'Digital Engagement Intern': 'Business Analyst',
  'Associate UX Designer': 'UX Design',
  'Jr. Developer': 'Fullstack Developer',
  'Software Developer': 'Fullstack Developer',
  'Senior Software Engineer': 'Backend Developer',
  'Frontend Developer': 'Frontend Developer',
  'Full Stack Developer': 'Fullstack Developer',
  'Database Administrator': 'Backend Developer',
  'Security Engineer': 'Backend Developer',
  'DevOps Engineer': 'Backend Developer',
  'Cloud Architect': 'Tech Lead',
  'Scrum Master': 'Business Analyst',
  'Data Analyst': 'Business Analyst',
  'QA Automation Engineer': 'QA',
  'Manual Tester': 'QA',
  'DevOps Specialist': 'Backend Developer',
  
  // GFT role mappings
  'L6': 'L6 - Project Head',
  'L5': 'L5 - Lead',
  'L4': 'L4 - Senior',
  'L3': 'L3 - Mid',
  
  // Incedo role mappings
  'Lead Software Engineer': 'Tech Lead',
  '.Net Sr Developer': 'Sr Developer',
  'Software Engineer': 'Software Engineer',
  'Lead - Technology': 'Lead - Technology',
  'Java Developer': 'Developer',
  'Python Developer': 'Developer',
  'React Developer': 'Developer'
};

/**
 * Reverse mapping from rate roles back to common roster roles
 * Used when updating roster members after rate role changes
 */
const REVERSE_ROLE_MAPPING = {
  'Fullstack Developer': ['Sr Dev', 'Jr Dev', 'Jr. Developer', 'Software Developer', 'Full Stack Developer'],
  'Business Analyst': ['Senior BA', 'Business Analyst', 'Investment System User Support Associate', 
                      'Investment System Support Senior Analyst', 'Investment Systems User Support Analyst',
                      'Investment Systems Support Manager', 'Senior Business Analyst, CRM', 'Senior Business Analyst',
                      'Business Analyst Intern', 'Senior Business Systems Analyst', 'Business Systems Analyst',
                      'Digital Engagement Service Delivery Manager', 'Digital Engagement Intern', 'Scrum Master', 'Data Analyst'],
  'Product': ['Product Manager', 'Director', 'Senior Director', 'Managing Director, Information Technology'],
  'UX Design': ['UX Designer', 'Associate UX Designer'],
  'Tech Lead': ['Tech Lead', 'Architect', 'Cloud Architect', 'Lead Software Engineer'],
  'QA': ['QA Lead', 'Jr QA', 'QA Automation Engineer', 'Manual Tester'],
  'Backend Developer': ['Senior Software Engineer', 'Database Administrator', 'Security Engineer', 'DevOps Engineer', 'DevOps Specialist'],
  'Frontend Developer': ['Frontend Developer'],
  'L6 - Project Head': ['L6'],
  'L5 - Lead': ['L5'],
  'L4 - Senior': ['L4'],
  'L3 - Mid': ['L3'],
  'Sr Developer': ['.Net Sr Developer'],
  'Software Engineer': ['Software Engineer'],
  'Lead - Technology': ['Lead - Technology'],
  'Developer': ['Java Developer', 'Python Developer', 'React Developer']
};

/**
 * Get standardized role name for rate lookup
 * This maps roster member roles to rate table roles
 */
export const getStandardizedRole = (originalRole) => {
  if (!originalRole) return '';
  return ROLE_MAPPING[originalRole] || originalRole;
};

/**
 * Get the most common roster role name for a given rate role
 * Used when updating roster members after rate changes
 */
export const getMostCommonRosterRole = (rateRole) => {
  if (!rateRole) return '';
  const commonRoles = REVERSE_ROLE_MAPPING[rateRole];
  return commonRoles && commonRoles.length > 0 ? commonRoles[0] : rateRole;
};

/**
 * Check if a roster role maps to a specific rate role
 * This only checks the role mapping, not the full company/location/role combination
 */
export const doesRosterRoleMapToRateRole = (rosterRole, rateRole) => {
  if (!rosterRole || !rateRole) return false;
  const standardizedRole = getStandardizedRole(rosterRole);
  return standardizedRole === rateRole;
};

/**
 * Check if a member's full metadata (company/location/role) maps to a specific rate
 * This is the primary function for validating member-to-rate mappings
 */
export const doesMemberMapToRate = (member, rate) => {
  if (!member || !rate) return false;
  
  const companyMatch = member.company === rate.company;
  const locationMatch = member.location === rate.location;
  const roleMatch = doesRosterRoleMapToRateRole(member.role, rate.role);
  
  return companyMatch && locationMatch && roleMatch;
};

/**
 * Find all members that map to a specific rate (company/location/role combination)
 */
export const findMembersForRate = (members, rate) => {
  if (!members || !rate) return [];
  
  return members.filter(member => doesMemberMapToRate(member, rate));
};

/**
 * Find all rates that a member could potentially use
 */
export const findRatesForMember = (rates, member) => {
  if (!rates || !member) return [];
  
  return rates.filter(rate => doesMemberMapToRate(member, rate));
};

/**
 * Get all roster roles that map to a specific rate role
 */
export const getRosterRolesForRateRole = (rateRole) => {
  if (!rateRole) return [];
  return REVERSE_ROLE_MAPPING[rateRole] || [rateRole];
};

/**
 * Validate that a member's complete metadata (company/location/role) properly maps to available rates
 */
export const validateMemberRoleMapping = async (member) => {
  if (!member || !member.company || !member.location || !member.role) {
    return {
      isValid: false,
      issue: 'Missing required member information (company, location, or role)',
      suggestions: [],
      memberKey: member ? `${member.company || 'MISSING'}/${member.location || 'MISSING'}/${member.role || 'MISSING'}` : 'INVALID_MEMBER'
    };
  }

  try {
    const ratesResponse = await api.getRates();
    const rates = ratesResponse?.rates || [];
    
    // Get standardized role for this member
    const standardizedRole = getStandardizedRole(member.role);
    const memberKey = `${member.company}/${member.location}/${member.role}`;
    const standardizedKey = `${member.company}/${member.location}/${standardizedRole}`;
    
    // Find all rates that this member could potentially use
    const matchingRates = findRatesForMember(rates, member);
    
    if (matchingRates.length > 0) {
      // Sort by year to get the most recent
      const sortedRates = matchingRates.sort((a, b) => (b.year || 2025) - (a.year || 2025));
      const bestMatch = sortedRates[0];
      
      return {
        isValid: true,
        standardizedRole,
        matchedRate: bestMatch,
        allMatchingRates: matchingRates,
        issue: null,
        suggestions: [],
        memberKey,
        standardizedKey,
        rateKey: `${bestMatch.company}/${bestMatch.location}/${bestMatch.role}`
      };
    }
    
    // No direct matches - provide detailed analysis
    const companyLocationRates = rates.filter(rate => 
      rate.company === member.company && 
      rate.location === member.location
    );
    
    const companyRates = rates.filter(rate => rate.company === member.company);
    const locationRates = rates.filter(rate => rate.location === member.location);
    
    let issue, suggestions;
    
    if (companyLocationRates.length === 0) {
      if (companyRates.length === 0) {
        issue = `No rates found for company '${member.company}'`;
        suggestions = [
          `Add rates for company '${member.company}'`,
          `Check if company name '${member.company}' matches exactly with rates table`,
          `Available companies: ${[...new Set(rates.map(r => r.company))].join(', ')}`
        ];
      } else {
        issue = `No rates found for location '${member.location}' at company '${member.company}'`;
        const companyLocations = [...new Set(companyRates.map(r => r.location))];
        suggestions = [
          `Add rates for ${member.company}/${member.location}`,
          `Available locations for ${member.company}: ${companyLocations.join(', ')}`,
          `Check if location name '${member.location}' matches exactly`
        ];
      }
    } else {
      // Company/location exists but role doesn't match
      const availableRoles = [...new Set(companyLocationRates.map(rate => rate.role))];
      issue = `No rate found for role '${standardizedRole}' at ${member.company}/${member.location}`;
      suggestions = [
        `Available roles at ${member.company}/${member.location}: ${availableRoles.join(', ')}`,
        `Member role '${member.role}' standardizes to '${standardizedRole}'`,
        `Consider updating member role or adding rate for '${standardizedRole}'`,
        `Add rate: ${member.company}/${member.location}/${standardizedRole}`
      ];
    }
    
    return {
      isValid: false,
      standardizedRole,
      issue,
      suggestions,
      availableRoles: companyLocationRates.map(r => r.role),
      memberKey,
      standardizedKey,
      companyLocationRates: companyLocationRates.length,
      companyRates: companyRates.length,
      locationRates: locationRates.length
    };
    
  } catch (error) {
    return {
      isValid: false,
      standardizedRole: getStandardizedRole(member.role),
      issue: `Error validating member mapping: ${error.message}`,
      suggestions: ['Check API connectivity', 'Verify rates data is available'],
      memberKey: `${member.company}/${member.location}/${member.role}`
    };
  }
};

/**
 * Calculate member salary from rates with COLA adjustment
 */
export const calculateMemberSalary = async (member, targetYear = new Date().getFullYear()) => {
  if (!member || !member.company || !member.location) {
    return {
      annualSalary: 0,
      monthlySalary: 0,
      dailyRate: 0,
      isAdjusted: false,
      adjustmentInfo: null,
      error: 'Missing member information'
    };
  }

  try {
    // Check if we're in quiet mode to reduce console noise during batch operations
    const isQuietMode = typeof window !== 'undefined' && window.rateCalculationQuietMode;
    
    // Get standardized role for rate lookup
    const standardizedRole = getStandardizedRole(member.role || '');
    
    if (!isQuietMode) {
      console.log(`calculateMemberSalary: ${member.name} - ${member.company}/${member.location}/${member.role} -> ${standardizedRole}`);
    }
    
        // Validate the role mapping first
    const validation = await validateMemberRoleMapping(member);
    if (!validation.isValid && !isQuietMode) {
      console.warn(`calculateMemberSalary: Role mapping issue for ${member.name}:`, validation.issue);
      // Continue anyway - the getRateWithCOLA function will handle fallbacks
    }
    
    // Get rate with COLA adjustment
    const rateData = await getRateWithCOLA(member.company, member.location, standardizedRole, targetYear);
    
        if (!rateData) {
      if (!isQuietMode) {
        console.warn(`calculateMemberSalary: No rate found for ${member.company}/${member.location}/${standardizedRole}`);
      }
      
      // Provide more detailed error information
      let errorDetails = `No rate found for ${member.company}/${member.location}/${standardizedRole}`;
      if (validation.suggestions && validation.suggestions.length > 0) {
        errorDetails += `. Suggestions: ${validation.suggestions.join('; ')}`;
      }
      
      return {
        annualSalary: 0,
        monthlySalary: 0,
        dailyRate: 0,
        isAdjusted: false,
        adjustmentInfo: null,
        error: errorDetails,
        roleMapping: {
          originalRole: member.role,
          standardizedRole,
          validationResult: validation
        }
      };
    }
    
    // Calculate annual and monthly salary based on rate type
    let annualSalary;
    let monthlySalary;
    
    // Validate and fix annualWorkingDays using the validation helper
    const originalWorkingDays = rateData.annualWorkingDays;
    rateData.annualWorkingDays = validateAnnualWorkingDays(rateData.annualWorkingDays, `calculateMemberSalary for ${member.name}`);
    
    // Log if we had to fix the working days
    if (originalWorkingDays !== rateData.annualWorkingDays) {
      console.log(`calculateMemberSalary: Fixed annualWorkingDays for ${member.name}: ${originalWorkingDays} → ${rateData.annualWorkingDays}`);
    }
    
    // Debug logging for rate calculation
    if (process.env.NODE_ENV === 'development') {
      console.log(`calculateMemberSalary: Rate calculation for ${member.name}:`, {
        dailyRate: rateData.dailyRate,
        annualWorkingDays: rateData.annualWorkingDays,
        isAdjusted: rateData.isAdjusted,
        targetYear,
        adjustmentInfo: rateData.adjustmentInfo
      });
    }
    
    if (rateData.isAdjusted && rateData.adjustmentInfo) {
      // For COLA-adjusted rates, apply COLA first then convert
      const colaAdjustedRate = calculateCOLAAdjustedRate(
        rateData.adjustmentInfo.baseRate,
        rateData.adjustmentInfo.baseYear,
        targetYear,
        rateData.adjustmentInfo.colaRate
      );
      
      // Validate COLA adjustment didn't produce unrealistic results
      if (colaAdjustedRate > rateData.adjustmentInfo.baseRate * 10) {
        console.warn(`calculateMemberSalary: COLA adjustment produced unrealistic rate for ${member.name}:`, {
          baseRate: rateData.adjustmentInfo.baseRate,
          colaAdjustedRate,
          baseYear: rateData.adjustmentInfo.baseYear,
          targetYear,
          colaRate: rateData.adjustmentInfo.colaRate
        });
      }
      
      if (rateData.annualWorkingDays === 1) {
        // Annual rate
        annualSalary = Math.round(colaAdjustedRate);
      } else {
        // Daily/other rate
        annualSalary = Math.round(colaAdjustedRate * rateData.annualWorkingDays);
      }
      
      monthlySalary = Math.round(annualSalary / 12);
    } else {
      // For non-adjusted rates, use original logic
      if (rateData.annualWorkingDays === 1) {
        // This is already an annual rate
        annualSalary = Math.round(rateData.dailyRate);
      } else {
        // Calculate from daily/other rate
        annualSalary = Math.round(rateData.dailyRate * rateData.annualWorkingDays);
        
        // Validate calculation
        if (rateData.dailyRate > 2000 && annualSalary > 500000) {
          console.warn(`calculateMemberSalary: High salary calculation for ${member.name}:`, {
            dailyRate: rateData.dailyRate,
            annualWorkingDays: rateData.annualWorkingDays,
            calculatedAnnual: annualSalary
          });
        }
      }
      
      monthlySalary = Math.round(annualSalary / 12);
    }
    
    // Final validation
    if (monthlySalary > 100000) {
      console.warn(`calculateMemberSalary: Very high monthly salary calculated for ${member.name}: $${monthlySalary}`);
    }
    
        if (!isQuietMode) {
      console.log(`calculateMemberSalary: ${member.name} calculated salary: $${annualSalary.toLocaleString()} annual, $${monthlySalary.toLocaleString()} monthly (${rateData.isAdjusted ? 'COLA adjusted' : 'base rate'})`);
      
      // Additional validation logging
      if (monthlySalary > 50000) {
        console.log(`calculateMemberSalary: High monthly rate details for ${member.name}:`, {
          company: member.company,
          location: member.location,
          role: member.role,
          standardizedRole,
          dailyRate: rateData.dailyRate,
          annualWorkingDays: rateData.annualWorkingDays,
          rateType: determineRateType(rateData.annualWorkingDays),
          isAdjusted: rateData.isAdjusted
        });
      }
    }
    
    return {
      annualSalary,
      monthlySalary,
      dailyRate: rateData.dailyRate,
      isAdjusted: rateData.isAdjusted,
      adjustmentInfo: rateData.adjustmentInfo,
      originalRate: rateData,
      standardizedRole,
      error: null,
      isFuzzyMatch: rateData.isFuzzyMatch,
      fuzzyMatchInfo: rateData.fuzzyMatchInfo,
      roleMapping: {
        originalRole: member.role,
        standardizedRole,
        validationResult: validation
      }
    };
    
  } catch (error) {
    console.error('Error calculating member salary:', error);
    return {
      annualSalary: 0,
      monthlySalary: 0,
      dailyRate: 0,
      isAdjusted: false,
      adjustmentInfo: null,
      error: error.message,
      roleMapping: {
        originalRole: member.role || '',
        standardizedRole: getStandardizedRole(member.role || ''),
        validationResult: null
      }
    };
  }
};

/**
 * Calculate salaries for multiple members
 */
export const calculateMemberSalaries = async (members, targetYear = new Date().getFullYear()) => {
  const results = {};
  
  for (const member of members) {
    if (member.id) {
      results[member.id] = await calculateMemberSalary(member, targetYear);
    }
  }
  
  return results;
};

/**
 * Detect and clean corrupted rate adjustment info immediately
 * This prevents the corruption from being repeatedly detected and processed
 */
export const immediateCorruptionCleanup = (roles) => {
  let cleanedCount = 0;
  const cleanedRoles = roles.map(role => {
    // Only check roles with adjustment info
    if (!role.rateAdjustmentInfo) {
      return role;
    }

    const adjustmentInfo = role.rateAdjustmentInfo;
    const baseRate = adjustmentInfo.baseRate;
    const annualWorkingDays = adjustmentInfo.annualWorkingDays;
    
    // Skip if we don't have the required data to detect corruption
    if (!baseRate || baseRate <= 0) {
      return role;
    }

    let needsCleanup = false;
    let corruptionReason = '';

    // Pattern 1: Annual rate (>50k) with daily working days (>200)
    if (baseRate > 50000 && annualWorkingDays && annualWorkingDays > 200) {
      needsCleanup = true;
      corruptionReason = `annual rate ${baseRate} with daily working days ${annualWorkingDays}`;
    }
    // Pattern 2: Broader corruption detection (>30k with >200 days)
    else if (baseRate > 30000 && annualWorkingDays && annualWorkingDays > 200) {
      needsCleanup = true;
      corruptionReason = `suspicious rate ${baseRate} with working days ${annualWorkingDays}`;
    }
    // Pattern 3: Known problematic base rates
    else if (baseRate === 163000 || baseRate === 164000) {
      needsCleanup = true;
      corruptionReason = `known problematic base rate ${baseRate}`;
    }
    // Pattern 4: Unrealistic monthly rates that would result from this adjustment
    else if (annualWorkingDays && annualWorkingDays > 200) {
      const wouldBeMonthlyRate = Math.round(baseRate * 21); // 21 business days per month
      if (wouldBeMonthlyRate > 100000) {
        needsCleanup = true;
        corruptionReason = `would produce unrealistic monthly rate ${wouldBeMonthlyRate}`;
      }
    }

    if (needsCleanup) {
      console.log(`🧹 immediateCorruptionCleanup: Cleaning corrupted adjustment info for ${role.name}: ${corruptionReason}`);
      cleanedCount++;
      
      return {
        ...role,
        rateAdjustmentInfo: null,
        calculatedForYear: null,
        lastRateUpdate: null,
        needsPersistence: true,
        corruptionCleaned: true
      };
    }

    return role;
  });

  if (cleanedCount > 0) {
    console.log(`🧹 immediateCorruptionCleanup: Cleaned ${cleanedCount} corrupted roles immediately`);
  }

  return {
    cleanedRoles,
    cleanedCount
  };
};

/**
 * Check if a role's rate needs to be refreshed based on last update time
 * Now also forces refresh to detect rate table changes and year mismatches
 */
export const shouldRefreshRate = (role, maxAgeHours = 24, targetYear = new Date().getFullYear()) => {
  // Only check assigned roles
  if (!role.roster_member_id) {
    return false;
  }
  
  // ALWAYS refresh if no update timestamp - this ensures new roles get processed
  if (!role.lastRateUpdate && !role.last_rate_update) {
    console.log(`shouldRefreshRate: Role ${role.name} has no rate update timestamp - needs refresh`);
    return true;
  }
  
  // Check if the role was calculated for a different year than target
  const calculatedForYear = role.calculatedForYear || role.timelineYear;
  if (calculatedForYear && calculatedForYear !== targetYear) {
    console.log(`shouldRefreshRate: Role ${role.name} calculated for year ${calculatedForYear}, but target is ${targetYear} - needs refresh`);
    return true;
  }
  
  // ALWAYS refresh if no calculatedForYear - this is critical for proper rate calculation
  if (!calculatedForYear && targetYear) {
    console.log(`shouldRefreshRate: Role ${role.name} has no calculatedForYear, needs refresh for year ${targetYear}`);
    return true;
  }
  
  // Check if role has rateAdjustmentInfo but rate seems to be from base year
  if (role.rateAdjustmentInfo && role.rateAdjustmentInfo.baseYear && role.rateAdjustmentInfo.baseRate) {
    try {
      // Validate adjustment info before using it
      const baseRate = role.rateAdjustmentInfo.baseRate;
      const baseYear = role.rateAdjustmentInfo.baseYear;
      const colaRate = role.rateAdjustmentInfo.colaRate || 4.5;
      const annualWorkingDays = role.rateAdjustmentInfo.annualWorkingDays; // Don't default to 252!
      
      // Validate inputs
      if (!baseRate || baseRate <= 0 || !baseYear || baseYear < 2020 || baseYear > 2030) {
        console.warn(`shouldRefreshRate: Invalid adjustment info for ${role.name}:`, {
          baseRate,
          baseYear,
          colaRate,
          annualWorkingDays
        });
        console.warn(`shouldRefreshRate: Invalid adjustment info detected for ${role.name}`);
        return true;
      }
      
      // Only check for corruption if annualWorkingDays is actually defined
      if (annualWorkingDays !== undefined && annualWorkingDays !== null) {
        // Detect corrupted data: if baseRate looks like annual (>50k) but annualWorkingDays suggests daily rate (>200)
        if (baseRate > 50000 && annualWorkingDays > 200) {
          console.warn(`shouldRefreshRate: Detected corrupted adjustment info for ${role.name}: baseRate ${baseRate} with annualWorkingDays ${annualWorkingDays}`);
          console.warn(`shouldRefreshRate: This suggests annual rate stored with daily rate working days - needs refresh`);
          // Immediately clean the corruption to prevent repeated detection
          role.rateAdjustmentInfo = null;
          role.calculatedForYear = null;
          role.lastRateUpdate = null;
          role.needsPersistence = true;
          role.corruptionCleaned = true;
          return true;
        }
        
        // Also detect broader corruption patterns (lowered threshold)
        if (baseRate > 30000 && annualWorkingDays > 200) {
          console.warn(`shouldRefreshRate: Detected potential corruption for ${role.name}: baseRate ${baseRate} with annualWorkingDays ${annualWorkingDays}`);
          console.warn(`shouldRefreshRate: Suspicious rate/working days combination - needs refresh`);
          // Immediately clean the corruption to prevent repeated detection
          role.rateAdjustmentInfo = null;
          role.calculatedForYear = null;
          role.lastRateUpdate = null;
          role.needsPersistence = true;
          role.corruptionCleaned = true;
          return true;
        }
      } else {
        // If annualWorkingDays is undefined in adjustment info, check if it's defined elsewhere
        const roleWorkingDays = role.annualWorkingDays || role.effectiveAnnualWorkingDays;
        if (roleWorkingDays && roleWorkingDays > 200 && baseRate > 30000) {
          console.warn(`shouldRefreshRate: Role ${role.name} has corrupted adjustment info: baseRate ${baseRate} with external workingDays ${roleWorkingDays}`);
          console.warn(`shouldRefreshRate: This would cause corruption - cleaning immediately`);
          // Immediately clean the corruption
          role.rateAdjustmentInfo = null;
          role.calculatedForYear = null;
          role.lastRateUpdate = null;
          role.needsPersistence = true;
          role.corruptionCleaned = true;
          return true;
        } else if (baseRate === 163000 || baseRate === 164000) {
          // Known corruption patterns with these specific base rates
          console.warn(`shouldRefreshRate: Role ${role.name} has suspicious adjustment info: baseRate ${baseRate} with undefined annualWorkingDays`);
          console.warn(`shouldRefreshRate: This is a known corruption pattern - cleaning immediately`);
          // Immediately clean the corruption
          role.rateAdjustmentInfo = null;
          role.calculatedForYear = null;
          role.lastRateUpdate = null;
          role.needsPersistence = true;
          role.corruptionCleaned = true;
          return true;
        } else {
          // If annualWorkingDays is undefined, we can't detect corruption this way
          console.log(`shouldRefreshRate: Role ${role.name} has adjustment info but no annualWorkingDays - cannot check for corruption`);
        }
      }
      
      // Convert expected rate to monthly for comparison - use role's annualWorkingDays if available
      const roleAnnualWorkingDays = role.annualWorkingDays || role.effectiveAnnualWorkingDays || annualWorkingDays;
      
      // Additional validation before calculation
      if (roleAnnualWorkingDays > 200 && baseRate > 50000) {
        console.warn(`shouldRefreshRate: Suspicious rate calculation inputs for ${role.name}: baseRate ${baseRate}, workingDays ${roleAnnualWorkingDays}`);
        console.warn(`shouldRefreshRate: This would result in unrealistic monthly rate - needs refresh`);
        // Immediately clean the corruption to prevent repeated detection
        role.rateAdjustmentInfo = null;
        role.calculatedForYear = null;
        role.lastRateUpdate = null;
        role.needsPersistence = true;
        role.corruptionCleaned = true;
        return true;
      }
      
      const expectedMonthlyRate = applyCOLAAndConvertToMonthly(
        baseRate,
        baseYear,
        targetYear,
        colaRate,
        roleAnnualWorkingDays
      );
      
      // Debug logging for rate calculation validation
      if (process.env.NODE_ENV === 'development' && expectedMonthlyRate > 20000) {
        console.log(`shouldRefreshRate: High expected rate calculation for ${role.name}:`, {
          baseRate,
          baseYear,
          targetYear,
          colaRate,
          roleAnnualWorkingDays,
          expectedMonthlyRate,
          currentRate: role.rate
        });
      }
      
      const currentRate = role.rate || 0;
      
      // Validate the expected rate is reasonable
      if (!expectedMonthlyRate || expectedMonthlyRate <= 0) {
        console.warn(`shouldRefreshRate: Invalid expected rate ${expectedMonthlyRate} for ${role.name}, needs refresh`);
        // Don't modify the role here - let cleanupCorruptedAdjustmentInfo handle it
        return true;
      }
      
      // Check for unrealistic rates that indicate data corruption
      if (expectedMonthlyRate > 100000) { // Reduced threshold to catch corruption earlier
        console.warn(`shouldRefreshRate: Unrealistic expected rate ${expectedMonthlyRate} for ${role.name}, corrupted adjustment info detected`);
        // Immediately clean the corruption to prevent repeated detection
        role.rateAdjustmentInfo = null;
        role.calculatedForYear = null;
        role.lastRateUpdate = null;
        role.needsPersistence = true;
        role.corruptionCleaned = true;
        return true;
      }
      
      const rateDifference = Math.abs(expectedMonthlyRate - currentRate);
      
      // Only refresh if there's a significant rate mismatch (more than $100)
      if (rateDifference > 100) {
        console.log(`shouldRefreshRate: Role ${role.name} has adjustment info but rate mismatch: expected ${expectedMonthlyRate}, current ${currentRate} - needs refresh`);
        return true;
      }
    } catch (error) {
      console.warn(`shouldRefreshRate: Error calculating expected rate for ${role.name}:`, error);
      console.warn(`shouldRefreshRate: Calculation error indicates corrupted adjustment info for ${role.name}`);
      // Immediately clean the corruption to prevent repeated detection
      role.rateAdjustmentInfo = null;
      role.calculatedForYear = null;
      role.lastRateUpdate = null;
      role.needsPersistence = true;
      role.corruptionCleaned = true;
      return true;
    }
  }
  
  // Check if role was recently cleaned up (has needsPersistence or corruptionCleaned flag)
  if (role.needsPersistence || role.corruptionCleaned) {
    console.log(`shouldRefreshRate: Role ${role.name} was recently cleaned up and needs persistence - forcing refresh`);
    return true;
  }
  
  // Check if role needs refresh based on age
  const lastUpdateTime = new Date(role.lastRateUpdate || role.last_rate_update);
  const now = new Date();
  const ageHours = (now - lastUpdateTime) / (1000 * 60 * 60);
  
  // Refresh if older than maxAgeHours OR if no rate is set
  if (ageHours > maxAgeHours || !role.rate || role.rate <= 0) {
    console.log(`shouldRefreshRate: Role ${role.name} needs refresh - age: ${Math.round(ageHours)}h, rate: ${role.rate}`);
    return true;
  }
  
  // For recently updated roles, only refresh if there's a specific issue detected above
  return false;
};

/**
 * Batch refresh rates for assigned members in roles array
 */
export const batchRefreshAssignedMemberRates = async (roles, roster, targetYear = new Date().getFullYear()) => {
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  let updatesCount = 0;
  const updatedRoles = await Promise.all(
    roles.map(async (role) => {
      // Only refresh rates for assigned members
      if (!role.roster_member_id) {
        return role;
      }
      
      // Check if this role needs refreshing based on age, year mismatch, or rate inconsistency
      const needsRefresh = shouldRefreshRate(role, 24, targetYear);
      
      const member = rosterLookup[role.roster_member_id];
      if (!member) {
        console.warn(`Member ${role.roster_member_id} not found in roster`);
        return role;
      }
      
      try {
        // Calculate latest member salary for the target year
        const memberSalaryInfo = await calculateMemberSalary(member, targetYear);
        
        if (memberSalaryInfo.monthlySalary > 0) {
          const currentRate = role.rate || 0;
          const newRate = memberSalaryInfo.monthlySalary;
          const rateDifference = Math.abs(newRate - currentRate);
          
          // Update if there's a meaningful difference (more than $1) or if refresh is needed
          if (rateDifference > 1 || needsRefresh) {
            console.log(`Updating rate for ${member.name}: ${currentRate} -> ${newRate} (target year: ${targetYear})`);
            updatesCount++;
            
            return {
              ...role,
              rate: newRate,
              rateAdjustmentInfo: memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null,
              lastRateUpdate: new Date().toISOString(),
              calculatedForYear: targetYear, // Track which year this rate was calculated for
              rateType: memberSalaryInfo.originalRate ? determineRateType(memberSalaryInfo.originalRate.annualWorkingDays) : 'monthly',
              annualWorkingDays: memberSalaryInfo.originalRate?.annualWorkingDays
            };
          }
        }
        
        // Update timestamp and year tracking even if rate didn't change
        return {
          ...role,
          lastRateUpdate: new Date().toISOString(),
          calculatedForYear: targetYear
        };
      } catch (error) {
        console.warn(`Could not refresh rate for member ${member.name}:`, error);
        return {
          ...role,
          lastRateUpdate: new Date().toISOString(),
          calculatedForYear: targetYear
        };
      }
    })
  );
  
  return {
    updatedRoles,
    updatesCount
  };
};

/**
 * Get rate update summary for a set of roles
 */
export const getRateUpdateSummary = (roles, targetYear) => {
  // If no target year provided, try to infer from roles or use current year
  if (!targetYear) {
    const firstAssignedRole = roles.find(r => r.roster_member_id && r.calculatedForYear);
    targetYear = firstAssignedRole?.calculatedForYear || new Date().getFullYear();
  }
  
  const assignedRoles = roles.filter(role => role.roster_member_id && role.roster_member_id !== '');
  const outdatedRoles = assignedRoles.filter(role => shouldRefreshRate(role, 24, targetYear));
  const recentlyUpdated = assignedRoles.filter(role => {
    if (!role.lastRateUpdate) return false;
    const lastUpdate = new Date(role.lastRateUpdate);
    const now = new Date();
    const ageMinutes = (now - lastUpdate) / (1000 * 60);
    return ageMinutes < 30; // Updated within last 30 minutes
  });
  
  return {
    totalAssigned: assignedRoles.length,
    outdated: outdatedRoles.length,
    recentlyUpdated: recentlyUpdated.length,
    needsRefresh: outdatedRoles.length > 0
  };
};

/**
 * Check if a role's metadata needs to be refreshed based on last update time
 * FIXED: Only return true if metadata has actually changed, not just based on time
 */
export const shouldRefreshRoleMetadata = (role, maxAgeHours = 24) => {
  // Only check assigned roles
  if (!role.roster_member_id) {
    return false;
  }
  
  // If no update timestamp, only refresh if it's been more than 1 hour since role creation
  // This prevents constant refreshing of newly created roles
  if (!role.lastMetadataUpdate) {
    // Check if role has a creation timestamp or other date field
    const creationTime = role.created_at || role.createdAt || role.last_rate_update || role.lastRateUpdate;
    if (creationTime) {
      const creation = new Date(creationTime);
      const now = new Date();
      const ageHours = (now - creation) / (1000 * 60 * 60);
      return ageHours > 1; // Only refresh if created more than 1 hour ago
    }
    // If no timestamps at all, only refresh if it's been more than 5 minutes since page load
    // This prevents immediate refresh on every page load
    const pageLoadTime = window.rosterPageLoadTime || Date.now();
    const ageMinutes = (Date.now() - pageLoadTime) / (1000 * 60);
    return ageMinutes > 5;
  }
  
  // FIXED: Don't refresh based on time alone - this was causing the infinite loop
  // The time-based check should only be used as a fallback when we can't determine
  // if metadata has actually changed. The actual change detection happens in
  // hasRoleMetadataChanged() function.
  return false;
};

/**
 * Check if role metadata has changed compared to roster member
 * This checks the complete Company/Location/Role combination plus other metadata
 */
export const hasRoleMetadataChanged = (role, member) => {
  if (!member || !role.roster_member_id) return false;
  
  // Normalize values to handle null/undefined/empty string differences
  const normalize = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };
  
  // Check if key metadata fields differ - Company/Location/Role are critical for rate mapping
  const normalizeDept = (value) => {
    if (!value) return '';
    return typeof value === 'object' ? String(value.name || '').trim() : String(value).trim();
  };
  const changes = {
    company: normalize(role.company) !== normalize(member.company),
    location: normalize(role.location) !== normalize(member.location),
    function: normalize(role.function) !== normalize(member.function),
    role: normalize(role.role) !== normalize(member.role),
    focus: normalize(role.focus) !== normalize(member.focus),
    department: normalizeDept(role.department) !== normalizeDept(member.department)
  };
  
  const hasChanges = Object.values(changes).some(changed => changed);
  
  // Only return true if there are actual meaningful changes
  if (hasChanges) {
    // Check if the Company/Location/Role combination changed (critical for rate mapping)
    const criticalChanges = {
      company: changes.company,
      location: changes.location,
      role: changes.role
    };
    
    const hasCriticalChanges = Object.values(criticalChanges).some(changed => changed);
    const currentKey = `${normalize(role.company)}/${normalize(role.location)}/${normalize(role.role)}`;
    const memberKey = `${normalize(member.company)}/${normalize(member.location)}/${normalize(member.role)}`;
    
    // Rate key OR department change both count as meaningful
    const hasMeaningfulChanges = currentKey !== memberKey || changes.department;

    if (hasMeaningfulChanges) {
      console.log(`Role metadata changes detected for ${member.name}:`, {
        currentRoleKey: currentKey,
        memberKey: memberKey,
        criticalChange: hasCriticalChanges,
        departmentChange: changes.department,
        changes,
        current: {
          company: role.company,
          location: role.location,
          function: role.function,
          role: role.role,
          focus: role.focus,
          department: role.department
        },
        member: {
          company: member.company,
          location: member.location,
          function: member.function,
          role: member.role,
          focus: member.focus,
          department: member.department
        }
      });

      if (hasCriticalChanges) {
        console.warn(`⚠️ Critical metadata change for ${member.name}: Company/Location/Role mapping will change from '${currentKey}' to '${memberKey}'`);
      }

      return true;
    } else {
      // Keys are identical and department unchanged - no meaningful changes
      console.log(`hasRoleMetadataChanged: ${member.name} - keys identical (${currentKey}), ignoring minor field differences`);
      return false;
    }
  }
  
  return false; // No changes detected
};

/**
 * Update role metadata to match roster member
 * Handles Company/Location/Role combination changes and their impact on rate mapping
 */
export const updateRoleMetadataFromMember = async (role, member, targetYear = new Date().getFullYear()) => {
  if (!member || !role.roster_member_id) {
    return role;
  }
  
  const originalKey = `${role.company}/${role.location}/${role.role}`;
  const newKey = `${member.company}/${member.location}/${member.role}`;
  const keyChanged = originalKey !== newKey;
  
  // Update metadata fields
  const updatedRole = {
    ...role,
    company: member.company || role.company,
    location: member.location || role.location,
    function: member.function || role.function,
    role: member.role || role.role,
    focus: member.focus || role.focus,
    department: member.department ?? role.department,
    lastMetadataUpdate: new Date().toISOString()
  };
  
  if (keyChanged) {
    console.log(`Company/Location/Role key change for ${member.name}: '${originalKey}' -> '${newKey}'`);
    
    // Clear previous rate information since the mapping key changed
    updatedRole.rateAdjustmentInfo = null;
    updatedRole.calculatedForYear = null;
    updatedRole.lastRateUpdate = null;
  }
  
  // Update rate since metadata changed (especially important for Company/Location/Role changes)
  try {
    const memberSalaryInfo = await calculateMemberSalary(member, targetYear);
    
    if (memberSalaryInfo.monthlySalary > 0) {
      updatedRole.rate = memberSalaryInfo.monthlySalary;
      updatedRole.rateAdjustmentInfo = memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null;
      updatedRole.lastRateUpdate = new Date().toISOString();
      updatedRole.calculatedForYear = targetYear;
      
      if (keyChanged) {
        console.log(`Rate updated for ${member.name} due to Company/Location/Role change: $${memberSalaryInfo.monthlySalary}/month`);
      }
    } else {
      // No rate found for new Company/Location/Role combination
      updatedRole.rate = 0;
      if (keyChanged && memberSalaryInfo.error) {
        console.warn(`No rate found for ${member.name} after Company/Location/Role change to '${newKey}': ${memberSalaryInfo.error}`);
      }
    }
    
    // Store the role mapping information for debugging
    if (memberSalaryInfo.roleMapping) {
      updatedRole.roleMapping = memberSalaryInfo.roleMapping;
    }
    
  } catch (error) {
    console.warn(`Could not update rate for member ${member.name} with new Company/Location/Role '${newKey}':`, error);
    if (keyChanged) {
      // Set rate to 0 if we can't calculate it after a key change
      updatedRole.rate = 0;
    }
  }
  
  return updatedRole;
};

/**
 * Batch refresh role metadata for assigned members in roles array
 */
export const batchRefreshAssignedMemberMetadata = async (roles, roster, targetYear = new Date().getFullYear()) => {
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  let metadataUpdatesCount = 0;
  let rateUpdatesCount = 0;
  
  const updatedRoles = await Promise.all(
    roles.map(async (role) => {
      // Only process assigned members
      if (!role.roster_member_id) {
        return role;
      }
      
      const member = rosterLookup[role.roster_member_id];
      if (!member) {
        console.warn(`Member ${role.roster_member_id} not found in roster`);
        return role;
      }
      
      // Check if metadata needs refreshing or has changed
      const needsMetadataRefresh = shouldRefreshRoleMetadata(role, 24);
      const hasMetadataChanged = hasRoleMetadataChanged(role, member);
      const needsRateRefresh = shouldRefreshRate(role, 24, targetYear);
      
      if (needsMetadataRefresh || hasMetadataChanged || needsRateRefresh) {
        try {
          const updatedRole = await updateRoleMetadataFromMember(role, member, targetYear);
          
          // Track what was updated
          if (hasMetadataChanged || needsMetadataRefresh) {
            metadataUpdatesCount++;
            console.log(`Updated metadata for ${member.name}`);
          }
          
          if (needsRateRefresh || hasMetadataChanged) {
            const rateDifference = Math.abs((updatedRole.rate || 0) - (role.rate || 0));
            if (rateDifference > 1) {
              rateUpdatesCount++;
              console.log(`Updated rate for ${member.name}: ${role.rate} -> ${updatedRole.rate}`);
            }
          }
          
          return updatedRole;
        } catch (error) {
          console.warn(`Could not refresh metadata for member ${member.name}:`, error);
          return {
            ...role,
            lastMetadataUpdate: new Date().toISOString()
          };
        }
      }
      
      return role;
    })
  );
  
  return {
    updatedRoles,
    metadataUpdatesCount,
    rateUpdatesCount,
    totalUpdatesCount: metadataUpdatesCount + rateUpdatesCount
  };
};

/**
 * Get role metadata update summary for a set of roles
 */
export const getRoleMetadataUpdateSummary = (roles, roster) => {
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  const assignedRoles = roles.filter(role => role.roster_member_id && role.roster_member_id !== '');
  
  const outdatedMetadata = assignedRoles.filter(role => shouldRefreshRoleMetadata(role, 24));
  
  const changedMetadata = assignedRoles.filter(role => {
    const member = rosterLookup[role.roster_member_id];
    return member && hasRoleMetadataChanged(role, member);
  });
  
  const recentlyUpdatedMetadata = assignedRoles.filter(role => {
    if (!role.lastMetadataUpdate) return false;
    const lastUpdate = new Date(role.lastMetadataUpdate);
    const now = new Date();
    const ageMinutes = (now - lastUpdate) / (1000 * 60);
    return ageMinutes < 30; // Updated within last 30 minutes
  });
  
  return {
    totalAssigned: assignedRoles.length,
    outdatedMetadata: outdatedMetadata.length,
    changedMetadata: changedMetadata.length,
    recentlyUpdatedMetadata: recentlyUpdatedMetadata.length,
    needsMetadataRefresh: outdatedMetadata.length > 0 || changedMetadata.length > 0
  };
};

/**
 * Combined refresh for both rates and metadata with timeline-based year calculation
 */
export const batchRefreshAssignedMembers = async (roles, roster, targetYear = new Date().getFullYear()) => {
  // First, immediately clean any corruption to prevent repeated detection
  const cleanupResult = immediateCorruptionCleanup(roles);
  if (cleanupResult.cleanedCount > 0) {
    console.log(`🧹 batchRefreshAssignedMembers: Cleaned ${cleanupResult.cleanedCount} corrupted roles before processing`);
  }
  
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  let metadataUpdatesCount = 0;
  let rateUpdatesCount = 0;
  const rolesToUpdate = []; // Track roles that need backend updates
  
  const updatedRoles = await Promise.all(
    roles.map(async (role) => {
      // Only process assigned members
      if (!role.roster_member_id) {
        return role;
      }
      
      const member = rosterLookup[role.roster_member_id];
      if (!member) {
        console.warn(`Member ${role.roster_member_id} not found in roster`);
        return role;
      }
      
      // Use timeline-specific year if available, otherwise fall back to targetYear
      const effectiveYear = role.timelineYear || targetYear;
      
      // Check what needs updating - pass effectiveYear to shouldRefreshRate
      const needsMetadataRefresh = shouldRefreshRoleMetadata(role, 24);
      const hasMetadataChanged = hasRoleMetadataChanged(role, member);
      const needsRateRefresh = shouldRefreshRate(role, 24, effectiveYear); // Pass effective year
      
      // Start with the original role - only update timestamps if actual changes are made
      let updatedRole = { ...role };
      let hasUpdates = false;
      
      // Only update calculatedForYear if it's missing or different
      if (!role.calculatedForYear || role.calculatedForYear !== effectiveYear) {
        updatedRole.calculatedForYear = effectiveYear;
        hasUpdates = true;
      }
      
      // Check if this role was cleaned up from corruption and needs persistence
      const wasCorruptionCleaned = role.needsPersistence || role.corruptionCleaned;
      
      // Skip processing if no updates are needed (unless corruption was cleaned)
      if (!needsMetadataRefresh && !hasMetadataChanged && !needsRateRefresh && !wasCorruptionCleaned) {
        return updatedRole; // Return early if no updates needed
      }
      
      if (needsMetadataRefresh || hasMetadataChanged || needsRateRefresh || wasCorruptionCleaned) {
        try {
          // FIXED: Only update metadata if it has actually changed
          if (hasMetadataChanged) {
            updatedRole = {
              ...updatedRole,
              company: member.company || role.company,
              location: member.location || role.location,
              function: member.function || role.function,
              role: member.role || role.role,
              focus: member.focus || role.focus,
              department: member.department ?? role.department,
              lastMetadataUpdate: new Date().toISOString() // Only update timestamp when metadata actually changes
            };
            
            metadataUpdatesCount++;
            hasUpdates = true;
            console.log(`Updated metadata for ${member.name}`);
          }
          
          // Update rate if needed (especially important if role/location changed or year mismatch)
          if (needsRateRefresh || hasMetadataChanged) {
            const memberSalaryInfo = await calculateMemberSalary(member, effectiveYear);
            
            if (memberSalaryInfo.monthlySalary > 0) {
              const currentRate = updatedRole.rate || 0;
              const newRate = memberSalaryInfo.monthlySalary;
              const rateDifference = Math.abs(newRate - currentRate);
              
              // Update if there's a meaningful difference (more than $1) or if year changed
              const yearChanged = (role.calculatedForYear || 0) !== effectiveYear;
              const rateChanged = rateDifference > 1;
              
              if (rateChanged || yearChanged) {
                updatedRole.rate = newRate;
                updatedRole.rateAdjustmentInfo = memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null;
                updatedRole.calculatedForYear = effectiveYear; // Track which year was used
                updatedRole.lastRateUpdate = new Date().toISOString(); // Only update timestamp when rate actually changes
                // Store fuzzy match information for UI warnings
                updatedRole.isFuzzyMatch = memberSalaryInfo.isFuzzyMatch;
                updatedRole.fuzzyMatchInfo = memberSalaryInfo.fuzzyMatchInfo;
                // Preserve or update annualWorkingDays from the rate calculation
                if (memberSalaryInfo.originalRate?.annualWorkingDays) {
                  updatedRole.annualWorkingDays = memberSalaryInfo.originalRate.annualWorkingDays;
                }
                rateUpdatesCount++;
                hasUpdates = true;
                
                if (rateChanged && yearChanged) {
                  console.log(`Updated rate for ${member.name}: ${currentRate} -> ${newRate} (year: ${effectiveYear}, rate and year changed)`);
                } else if (rateChanged) {
                  console.log(`Updated rate for ${member.name}: ${currentRate} -> ${newRate} (year: ${effectiveYear}, rate changed)`);
                } else if (yearChanged) {
                  console.log(`Updated rate for ${member.name}: ${currentRate} -> ${newRate} (year: ${effectiveYear}, year changed)`);
                }
              }
            }
          }
          
          // Track roles that had actual updates or corruption cleanup for backend persistence
          if (hasUpdates || wasCorruptionCleaned) {
            const updateReason = [];
            if (hasUpdates) updateReason.push('data updated');
            if (wasCorruptionCleaned) updateReason.push('corruption cleaned');
            
            rolesToUpdate.push({
              originalRole: role,
              updatedRole: {
                ...updatedRole,
                needsPersistence: undefined, // Remove the flag after tracking
                corruptionCleaned: undefined // Remove the flag after tracking
              },
              memberId: member.id,
              memberName: member.name,
              wasCorruptionCleanup: wasCorruptionCleaned,
              updateReason: updateReason.join(', ')
            });
            
            console.log(`batchRefreshAssignedMembers: Added ${member.name} to persistence queue (${updateReason.join(', ')})`);
            
            // Clear the flags since we're tracking them
            if (role.needsPersistence) {
              role.needsPersistence = undefined;
            }
            if (role.corruptionCleaned) {
              role.corruptionCleaned = undefined;
            }
          }
          
          return updatedRole;
        } catch (error) {
          console.warn(`Could not refresh data for member ${member.name}:`, error);
          return updatedRole; // Still return with updated timestamps
        }
      }
      
      return updatedRole;
    })
  );
  
  // Log summary of what was updated
  if (rolesToUpdate.length > 0) {
    console.log(`batchRefreshAssignedMembers: Updated ${rolesToUpdate.length} roles:`, 
      rolesToUpdate.map(r => `${r.memberName} (${r.memberId})`));
  }
  
  return {
    updatedRoles,
    metadataUpdatesCount,
    rateUpdatesCount,
    totalUpdatesCount: metadataUpdatesCount + rateUpdatesCount,
    rolesToUpdate // Include this for components that need to persist updates
  };
};

/**
 * Get combined update summary for both rates and metadata
 */
export const getCombinedUpdateSummary = (roles, roster, targetYear) => {
  // If no target year provided, try to infer from roles or use current year
  if (!targetYear) {
    const firstAssignedRole = roles.find(r => r.roster_member_id && r.calculatedForYear);
    targetYear = firstAssignedRole?.calculatedForYear || new Date().getFullYear();
  }
  
  const ratesSummary = getRateUpdateSummary(roles, targetYear);
  const metadataSummary = getRoleMetadataUpdateSummary(roles, roster);
  
  return {
    ...ratesSummary,
    ...metadataSummary,
    needsAnyRefresh: ratesSummary.needsRefresh || metadataSummary.needsMetadataRefresh
  };
};

/**
 * Verify that silent refresh actually updated the data
 * This helps debug refresh issues by comparing before/after states
 */
export const verifySilentRefreshResults = (beforeRoles, afterRoles, expectedUpdates) => {
  const verification = {
    rateChanges: 0,
    metadataChanges: 0,
    timestampUpdates: 0,
    unexpectedChanges: 0,
    verificationPassed: false
  };
  
  if (!beforeRoles || !afterRoles || beforeRoles.length !== afterRoles.length) {
    console.warn('verifySilentRefreshResults: Invalid role arrays for verification');
    return verification;
  }
  
  beforeRoles.forEach((beforeRole, index) => {
    const afterRole = afterRoles[index];
    if (!afterRole || beforeRole.id !== afterRole.id) {
      verification.unexpectedChanges++;
      return;
    }
    
    // Check for rate changes
    if (Math.abs((afterRole.rate || 0) - (beforeRole.rate || 0)) > 1) {
      verification.rateChanges++;
      console.log(`verifySilentRefreshResults: Rate updated for role ${afterRole.name}: ${beforeRole.rate} -> ${afterRole.rate}`);
    }
    
    // Check for metadata changes
    const metadataFields = ['company', 'location', 'function', 'role', 'focus'];
    const hasMetadataChange = metadataFields.some(field => beforeRole[field] !== afterRole[field]);
    if (hasMetadataChange) {
      verification.metadataChanges++;
      console.log(`verifySilentRefreshResults: Metadata updated for role ${afterRole.name}`);
    }
    
    // Check for timestamp updates
    if (afterRole.lastRateUpdate !== beforeRole.lastRateUpdate || 
        afterRole.lastMetadataUpdate !== beforeRole.lastMetadataUpdate) {
      verification.timestampUpdates++;
    }
  });
  
  // Verify that we got the expected number of updates
  const totalActualUpdates = verification.rateChanges + verification.metadataChanges;
  const totalExpectedUpdates = (expectedUpdates?.rateUpdatesCount || 0) + (expectedUpdates?.metadataUpdatesCount || 0);
  
  verification.verificationPassed = totalActualUpdates >= totalExpectedUpdates && verification.unexpectedChanges === 0;
  
  console.log('verifySilentRefreshResults: Verification results:', {
    expected: expectedUpdates,
    actual: verification,
    passed: verification.verificationPassed
  });
  
  return verification;
};

/**
 * Detect roles that have backwards compatibility issues with rate calculations or timeline data
 */
export const detectBackwardsCompatibilityIssues = (roles, targetYear = new Date().getFullYear()) => {
  const issues = [];
  
  roles.forEach(role => {
    const roleName = role.name || 'Unknown Role';
    const currentRate = role.rate || 0;
    
    // Issue 1: Role missing dateRanges (timeline data)
    if (!role.dateRanges || role.dateRanges.length === 0) {
      issues.push({
        type: 'missing_timeline',
        roleId: role.id,
        roleName,
        memberId: role.roster_member_id || null,
        issue: `Role missing dateRanges timeline data - will default to milestone dates`,
        severity: 'warning' // This is handled gracefully
      });
    }
    
    // Only check rate issues for assigned roles
    if (!role.roster_member_id) return;
    
    // Issue 2: Role has rateAdjustmentInfo but calculatedForYear is wrong
    if (role.rateAdjustmentInfo && role.calculatedForYear !== targetYear) {
      issues.push({
        type: 'year_mismatch',
        roleId: role.id,
        roleName,
        memberId: role.roster_member_id,
        issue: `Role calculated for year ${role.calculatedForYear || 'unknown'} but target is ${targetYear}`,
        currentRate,
        calculatedForYear: role.calculatedForYear,
        targetYear,
        severity: 'error'
      });
    }
    
    // Issue 3: Role has rateAdjustmentInfo but rate doesn't match expected COLA adjustment
    if (role.rateAdjustmentInfo && role.rateAdjustmentInfo.baseYear && role.rateAdjustmentInfo.baseRate) {
      const expectedRate = calculateCOLAAdjustedRate(
        role.rateAdjustmentInfo.baseRate,
        role.rateAdjustmentInfo.baseYear,
        targetYear,
        role.rateAdjustmentInfo.colaRate || 4.5
      );
      
      // Convert to monthly using proper rate type detection
      const expectedMonthlyRate = convertToMonthly(expectedRate, role.rateAdjustmentInfo.annualWorkingDays || 252);
      const rateDifference = Math.abs(expectedMonthlyRate - currentRate);
      
      if (rateDifference > 1) {
        issues.push({
          type: 'rate_mismatch',
          roleId: role.id,
          roleName,
          memberId: role.roster_member_id,
          issue: `Rate ${currentRate} doesn't match expected COLA-adjusted rate ${expectedMonthlyRate}`,
          currentRate,
          expectedRate: expectedMonthlyRate,
          rateDifference,
          adjustmentInfo: role.rateAdjustmentInfo,
          severity: 'error'
        });
      }
    }
    
    // Issue 4: Role has old calculatedForYear (2025) but should be using project year
    if (role.calculatedForYear === 2025 && targetYear > 2025) {
      issues.push({
        type: 'outdated_year',
        roleId: role.id,
        roleName,
        memberId: role.roster_member_id,
        issue: `Role still using 2025 rates but project is for ${targetYear}`,
        currentRate,
        calculatedForYear: role.calculatedForYear,
        targetYear,
        severity: 'warning'
      });
    }
  });
  
  return issues;
};

/**
 * Fix backwards compatibility issues by forcing a rate refresh
 */
export const fixBackwardsCompatibilityIssues = async (roles, roster, issues, targetYear = new Date().getFullYear()) => {
  if (issues.length === 0) {
    return { updatedRoles: roles, fixedCount: 0 };
  }
  
  console.log(`fixBackwardsCompatibilityIssues: Fixing ${issues.length} backwards compatibility issues`);
  
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  let fixedCount = 0;
  const updatedRoles = await Promise.all(
    roles.map(async (role) => {
      const roleIssues = issues.filter(issue => issue.roleId === role.id);
      if (roleIssues.length === 0) {
        return role;
      }
      
      const member = rosterLookup[role.roster_member_id];
      if (!member) {
        console.warn(`fixBackwardsCompatibilityIssues: Member ${role.roster_member_id} not found`);
        return role;
      }
      
      try {
        // Force recalculate the rate for the target year
        const memberSalaryInfo = await calculateMemberSalary(member, targetYear);
        
        if (memberSalaryInfo.monthlySalary > 0) {
          const oldRate = role.rate || 0;
          const newRate = memberSalaryInfo.monthlySalary;
          
          console.log(`fixBackwardsCompatibilityIssues: Fixed ${member.name}: ${oldRate} -> ${newRate} (year: ${targetYear})`);
          fixedCount++;
          
          return {
            ...role,
            rate: newRate,
            rateAdjustmentInfo: memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null,
            lastRateUpdate: new Date().toISOString(),
            calculatedForYear: targetYear,
            backwardsCompatibilityFixed: true
          };
        }
      } catch (error) {
        console.error(`fixBackwardsCompatibilityIssues: Error fixing role for ${member.name}:`, error);
      }
      
      return role;
    })
  );
  
  console.log(`fixBackwardsCompatibilityIssues: Fixed ${fixedCount} roles`);
  
  return { updatedRoles, fixedCount };
};

/**
 * Enhanced silent refresh with verification and retry logic
 */
export const performVerifiedSilentRefresh = async (roles, roster, outcomeId, targetYear, maxRetries = 2) => {
  // If no target year provided, try to infer from roles or use current year
  if (!targetYear) {
    // Try to get year from first role's calculated year or use current year
    const firstAssignedRole = roles.find(r => r.roster_member_id && r.calculatedForYear);
    targetYear = firstAssignedRole?.calculatedForYear || new Date().getFullYear();
    console.log(`performVerifiedSilentRefresh: No target year provided, using ${targetYear}`);
  }
  if (!shouldPerformSilentRefresh(roles, roster, targetYear)) {
    return {
      updated: false,
      rateUpdatesCount: 0,
      metadataUpdatesCount: 0,
      updatedRoles: roles,
      verified: true
    };
  }
  
  console.log('performVerifiedSilentRefresh: Starting verified silent refresh for outcome', outcomeId);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const beforeRoles = JSON.parse(JSON.stringify(roles)); // Deep copy for comparison
      
      // Try backend refresh first if outcomeId is provided
      if (outcomeId) {
        try {
          const refreshResult = await api.refreshOutcomeRates(outcomeId, targetYear);
          console.log(`performVerifiedSilentRefresh: Backend refresh completed (attempt ${attempt}):`, refreshResult);
          
          // Fetch fresh data to verify the refresh worked
          const freshStaffingData = await api.getStaffingData(outcomeId);
          const staffingData = freshStaffingData.data || freshStaffingData || {};
          
          if (staffingData.milestones) {
            const freshRoles = staffingData.milestones.flatMap(milestone => milestone.roles || []);
            const verification = verifySilentRefreshResults(beforeRoles, freshRoles, refreshResult);
            
            if (verification.verificationPassed || attempt === maxRetries) {
              // Extract rate and metadata updates from the backend response
              const rateUpdates = refreshResult.rate_updates || [];
              const metadataUpdates = refreshResult.metadata_updates || [];
              
                            return {
                updated: true,
                rateUpdatesCount: rateUpdates.length,
                metadataUpdatesCount: metadataUpdates.length,
                updatedRoles: freshRoles,
                verified: true,
                backendRefresh: true,
                attempt
              };
            }
          }
        } catch (backendError) {
          console.warn(`performVerifiedSilentRefresh: Backend refresh failed (attempt ${attempt}):`, backendError);
          // Fall through to frontend refresh
        }
      }
      
      // Fallback to frontend refresh
      const refreshResult = await batchRefreshAssignedMembers(roles, roster, targetYear);
      const verification = verifySilentRefreshResults(beforeRoles, refreshResult.updatedRoles, refreshResult);
      
      if (verification.verificationPassed || attempt === maxRetries) {
        return {
          updated: refreshResult.totalUpdatesCount > 0,
          rateUpdatesCount: refreshResult.rateUpdatesCount,
          metadataUpdatesCount: refreshResult.metadataUpdatesCount,
          updatedRoles: refreshResult.updatedRoles,
          verified: verification.verificationPassed,
          frontendRefresh: true,
          attempt
        };
      }
      
      console.warn(`performVerifiedSilentRefresh: Verification failed on attempt ${attempt}, retrying...`);
      
    } catch (error) {
      console.error(`performVerifiedSilentRefresh: Attempt ${attempt} failed:`, error);
      
      if (attempt === maxRetries) {
        return {
          updated: false,
          rateUpdatesCount: 0,
          metadataUpdatesCount: 0,
          updatedRoles: roles,
          verified: false,
          error: error.message,
          attempt
        };
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
};

/**
 * Check if silent refresh should be performed based on role state
 */
export const shouldPerformSilentRefresh = (roles, roster, targetYear) => {
  const summary = getCombinedUpdateSummary(roles, roster, targetYear);
  return summary.needsAnyRefresh;
};

/**
 * Notify other components that outcome rates have been updated
 */
const notifyOutcomeRatesUpdated = (outcomeIds, updateInfo) => {
  if (typeof window !== 'undefined') {
    const event = new CustomEvent('outcomeRatesUpdated', {
      detail: { outcomeIds, updateInfo }
    });
    window.dispatchEvent(event);
  }
};

/**
 * Invalidate outcome caches to force fresh data fetches
 */
const invalidateOutcomeCaches = (outcomeIds) => {
  if (typeof window !== 'undefined' && window.outcomeCache) {
    outcomeIds.forEach(outcomeId => {
      delete window.outcomeCache[outcomeId];
    });
  }
};

/**
 * Legacy function name - alias for immediateCorruptionCleanup
 * @deprecated Use immediateCorruptionCleanup instead
 */
export const cleanupCorruptedAdjustmentInfo = (roles) => {
  console.warn('cleanupCorruptedAdjustmentInfo is deprecated, use immediateCorruptionCleanup instead');
  return immediateCorruptionCleanup(roles);
};

/**
 * Debug function to analyze role refresh status and provide detailed information
 */
export const debugRoleRefreshStatus = (roles, roster, targetYear = new Date().getFullYear()) => {
  const rosterLookup = roster.reduce((acc, member) => {
    acc[member.id] = member;
    return acc;
  }, {});
  
  const debugInfo = {
    totalRoles: roles.length,
    assignedRoles: 0,
    unassignedRoles: 0,
    rolesNeedingRefresh: 0,
    rolesWithCorruption: 0,
    rolesWithoutRates: 0,
    rolesWithYearMismatch: 0,
    roleDetails: []
  };
  
  roles.forEach(role => {
    const isAssigned = !!role.roster_member_id;
    const member = isAssigned ? rosterLookup[role.roster_member_id] : null;
    
    if (isAssigned) {
      debugInfo.assignedRoles++;
    } else {
      debugInfo.unassignedRoles++;
      return; // Skip unassigned roles
    }
    
    const needsRefresh = shouldRefreshRate(role, 24, targetYear);
    const hasCorruption = role.rateAdjustmentInfo && (
      (role.rateAdjustmentInfo.baseRate > 50000 && role.rateAdjustmentInfo.annualWorkingDays > 200) ||
      role.rateAdjustmentInfo.baseRate === 163000 ||
      role.rateAdjustmentInfo.baseRate === 164000
    );
    const hasRate = role.rate && role.rate > 0;
    const hasYearMismatch = role.calculatedForYear && role.calculatedForYear !== targetYear;
    
    if (needsRefresh) debugInfo.rolesNeedingRefresh++;
    if (hasCorruption) debugInfo.rolesWithCorruption++;
    if (!hasRate) debugInfo.rolesWithoutRates++;
    if (hasYearMismatch) debugInfo.rolesWithYearMismatch++;
    
    debugInfo.roleDetails.push({
      id: role.id,
      name: role.name,
      memberId: role.roster_member_id,
      memberName: member?.name || 'Unknown',
      rate: role.rate || 0,
      calculatedForYear: role.calculatedForYear,
      needsRefresh,
      hasCorruption,
      hasRate,
      hasYearMismatch,
      lastRateUpdate: role.lastRateUpdate,
      rateAdjustmentInfo: role.rateAdjustmentInfo ? {
        baseRate: role.rateAdjustmentInfo.baseRate,
        baseYear: role.rateAdjustmentInfo.baseYear,
        annualWorkingDays: role.rateAdjustmentInfo.annualWorkingDays
      } : null,
      issues: [
        needsRefresh && 'needs refresh',
        hasCorruption && 'has corruption',
        !hasRate && 'no rate',
        hasYearMismatch && `year mismatch (${role.calculatedForYear} vs ${targetYear})`
      ].filter(Boolean)
    });
  });
  
  // Sort role details by number of issues (most problematic first)
  debugInfo.roleDetails.sort((a, b) => b.issues.length - a.issues.length);
  
  console.group('🔍 Role Refresh Status Debug');
  console.log('Summary:', {
    totalRoles: debugInfo.totalRoles,
    assignedRoles: debugInfo.assignedRoles,
    unassignedRoles: debugInfo.unassignedRoles,
    rolesNeedingRefresh: debugInfo.rolesNeedingRefresh,
    rolesWithCorruption: debugInfo.rolesWithCorruption,
    rolesWithoutRates: debugInfo.rolesWithoutRates,
    rolesWithYearMismatch: debugInfo.rolesWithYearMismatch
  });
  
  if (debugInfo.rolesNeedingRefresh > 0) {
    console.log('Roles needing refresh:', debugInfo.roleDetails.filter(r => r.needsRefresh));
  }
  
  if (debugInfo.rolesWithCorruption > 0) {
    console.warn('Roles with corruption:', debugInfo.roleDetails.filter(r => r.hasCorruption));
  }
  
  if (debugInfo.rolesWithYearMismatch > 0) {
    console.log('Roles with year mismatch:', debugInfo.roleDetails.filter(r => r.hasYearMismatch));
  }
  
  console.groupEnd();
  
  return debugInfo;
};

